import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, PackageOpen } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { WithdrawalNoteStatusBadge } from '@/components/warehouse/withdrawal-status-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCancelPurchaseWithdrawalNote,
    usePurchaseWithdrawalNote,
} from '@/hooks/use-purchase-lot-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/withdrawal-notes/$id',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const { data, isLoading } = usePurchaseWithdrawalNote(id)
    const note = data?.data
    const { user, hasPermission } = useAuth()
    const cancel = useCancelPurchaseWithdrawalNote()

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isOpen = note.status === 'open'
    const isCreator = user?.id && note.created_by_id === user.id
    const canWrite = hasPermission('purchase_lots.write')
    const canCancel = isOpen && isCreator && canWrite

    const handleCancel = async () => {
        if (!confirm(m.pl_plh_confirm_cancel())) return
        try {
            await cancel.mutateAsync(id)
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_cancel(),
            )
        }
    }

    return (
        <div className="space-y-4 p-4 md:p-6">
            <Button
                asChild
                variant="ghost"
                className="h-11 md:h-9 -ml-2 md:ml-0"
            >
                <Link to="/purchase-lots/withdrawal-notes" search={{ page: 1 }}>
                    <ArrowLeft className="size-4 mr-1" /> {m.sl_lot_back()}
                </Link>
            </Button>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <PackageOpen className="size-5" />
                            <CardTitle className="font-mono text-base md:text-2xl">
                                {note.code}
                            </CardTitle>
                            <WithdrawalNoteStatusBadge status={note.status} />
                        </div>
                        {canCancel && (
                            <Button
                                className="h-11 md:h-9"
                                variant="outline"
                                onClick={() => void handleCancel()}
                                disabled={cancel.isPending}
                            >
                                {m.sl_detail_action_cancel()}
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <Field
                        label={m.sl_detail_label_created_at()}
                        value={formatDateTimeVN(note.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={note.created_by_name ?? '—'}
                    />
                    {note.notes && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_notes()}
                                value={note.notes}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.pl_item_col_lot()}</TableHead>
                            <TableHead>{m.pl_item_col_purchase()}</TableHead>
                            <TableHead>{m.pl_item_col_grower()}</TableHead>
                            <TableHead className="text-right">
                                {m.pl_item_col_quantity()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(note.items ?? []).map((it) => (
                            <TableRow key={it.id}>
                                <TableCell className="font-mono text-xs">
                                    <Link
                                        to="/purchase-lots/lots/$id"
                                        params={{ id: it.purchase_lot_id }}
                                        className="text-primary hover:underline"
                                    >
                                        {it.purchase_lot_code ??
                                            it.purchase_lot_id}
                                    </Link>
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                    {it.purchase_code ?? '—'}
                                </TableCell>
                                <TableCell>{it.grower_name ?? '—'}</TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatQty(it.quantity)} kg
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
